import { PROTOCOL_PADDING } from 'components/canvas/components/nodes/components/protocol-node/index';
import useFetchSurfaceNodesFromCache from 'components/canvas/hooks/use-fetch-surface-nodes-from-cache';
import useCanvas from 'components/canvas/hooks/use-canvas';
import useNodeDimensionsOperations from 'components/canvas/hooks/use-node-dimensions-operations';
import useUpdateNodePosition from 'components/canvas/hooks/use-update-node-position';
import useSaveNodeInCache from 'components/canvas/hooks/use-save-node-in-cache';
import useEjectSurfaceNodeFromSurfaceProtocolNode from 'components/canvas/components/nodes/components/base-node/hooks/use-eject-surface-node-from-surface-protocol-node';
import useAddSurfaceNodeToSurfaceProtocolNode from 'components/canvas/components/nodes/components/base-node/hooks/use-add-surface-node-to-surface-protocol-node';
import { isNull, omit } from 'lodash';
import {
  SurfaceNodeType,
  SurfaceNodeUnion,
  SurfaceProtocolNode,
} from 'graphql/types';
import { Position } from 'types';
import Konva from 'konva';
import { compose, set } from 'lodash/fp';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import { addNodeToSurfaceInCache } from 'components/canvas/hooks/use-add-node-to-surface-in-cache';
import useFetchNodeFromCacheById from 'components/canvas/hooks/use-fetch-node-from-cache-by-id';

interface PositinIdInput {
  position: Position;
  id: string;
}

interface UseProtocolOperations {
  shouldEjectNodeFromProtocol(surfaceNode: SurfaceNodeUnion): boolean;
  resizeProtocolToFitNode(
    node: SurfaceNodeUnion,
    protocolNode: SurfaceProtocolNode,
  ): void;
  resizeProtocolToOriginalSize(protocolNode: SurfaceProtocolNode): void;
  shouldContainNode(position: Position): boolean;
  shouldRepositionContainingNodeInProtocol(input: PositinIdInput): boolean;
  getProtocolNodeDraggedInto(
    position: Position,
  ): SurfaceProtocolNode | undefined;
  ejectNodeFromProtocol({
    protocolNode,
    position,
  }: {
    protocolNode: SurfaceProtocolNode;
    position: Position;
  }): Promise<any>;
  placeNodeInProtocol(input: PositinIdInput): Promise<any>;
  (position: Position, protocolNode: SurfaceProtocolNode): boolean;
  ({ position, id }: { position: Position; id: string }): void;
  shapesDoNotHaveIntersection(
    shape: Konva.Node,
    otherShape: Konva.Node,
  ): boolean;
  shapesHaveIntersection(shape: Konva.Node, otherShape: Konva.Node): boolean;
  isPositionOverProtocolNode(
    position: Position,
    protocolNode: SurfaceProtocolNode,
  ): boolean;
  repositionContainingNodeInProtocol(input: PositinIdInput): void;
  findProtocolContainingNodeShape(
    referenceProtocol: SurfaceProtocolNode,
  ): Konva.Node | null;
}

interface UseProtocolNodePositionOperations {
  updateProtocolNodePositionInCache(
    node: SurfaceNodeUnion,
    position: Position,
  ): void;
}

export function useProtocolNodePositionOperations(): UseProtocolNodePositionOperations {
  const saveNodeInCache = useSaveNodeInCache();

  function updateProtocolNodePositionInCache(
    node: SurfaceNodeUnion,
    position: Position,
  ) {
    if (position) {
      let newNode = compose(
        set('position.x', position.x),
        set('position.y', position.y),
      )(node);

      const referenceIsOnSameSurface =
        node.isReference &&
        node?.surface &&
        node?.surface?.id === node?.originalNode?.surface?.id;

      //@ts-ignore
      if (node.contains) {
        if (referenceIsOnSameSurface) {
          // return saveNodeInCache(omit(newNode, ['contains']) as any);
        }
      }

      saveNodeInCache(newNode);
    }
  }

  return { updateProtocolNodePositionInCache };
}

export default function useProtocolOperations() {
  const { id: surfaceId } = useCurrentSurface() || {};

  const { getNodeDimensionsByType } = useNodeDimensionsOperations();

  const { updateNodePositionInCache } = useUpdateNodePosition();

  const { findShapeById } = useCanvas();

  const fetchSurfaceNodesFromCache = useFetchSurfaceNodesFromCache(surfaceId);

  const fetchNodeFromCacheById = useFetchNodeFromCacheById();

  const nodeDimensions = getNodeDimensionsByType(SurfaceNodeType.Protocol);

  const ejectSurfaceNodeFromSurfaceProtocolNode = useEjectSurfaceNodeFromSurfaceProtocolNode();

  const addSurfaceNodeToSurfaceProtocolNode = useAddSurfaceNodeToSurfaceProtocolNode(
    surfaceId,
  );

  function fetchProtocolsFromCache(): SurfaceProtocolNode[] {
    return fetchSurfaceNodesFromCache().filter(
      (node) => node.type === SurfaceNodeType.Protocol,
    );
  }

  function getProtocolNodeDraggedInto(
    position?: Position,
  ): SurfaceProtocolNode | null {
    if (position) {
      return (
        fetchProtocolsFromCache().find((protocolNode) =>
          isHoveringOverProtocolNode(position, protocolNode),
        ) || null
      );
    }

    return null;
  }

  function shouldContainNode(position: Position): boolean {
    const protocolNodeDraggedInto = getProtocolNodeDraggedInto(position);

    return Boolean(
      protocolNodeDraggedInto?.position &&
        isNull(protocolNodeDraggedInto?.contains),
    );
  }

  function shouldRepositionContainingNodeInProtocol(
    containingNode: SurfaceNodeUnion,
  ): boolean {
    const protocolNodeDraggedInto = getProtocolNodeDraggedInto(
      containingNode.position as Position,
    );

    const protocolNodeToRemoveFrom = fetchProtocolFromContainingNode(
      containingNode,
    );

    return Boolean(
      protocolNodeDraggedInto &&
        protocolNodeToRemoveFrom &&
        protocolNodeToRemoveFrom?.id === protocolNodeDraggedInto?.id,
    );
  }

  function resizeProtocolToOriginalSize(
    protocolNode: SurfaceProtocolNode,
  ): void {
    const protocolShapeRectInstance = findShapeById('rect-' + protocolNode.id);
    const protocolShapeContextMenuInstance = findShapeById(
      'context-menu-' + protocolNode.id,
    );

    const dimensions = getNodeDimensionsByType(SurfaceNodeType.Protocol);

    protocolShapeContextMenuInstance?.x(dimensions.width);
    protocolShapeRectInstance?.width(dimensions.width);
    protocolShapeRectInstance?.height(dimensions.height);
  }

  function resizeProtocolToFitNode(
    node: SurfaceNodeUnion,
    protocolNode: SurfaceProtocolNode,
  ): void {
    const protocolShapeRectInstance = findShapeById('rect-' + protocolNode.id);

    const protocolShapeContextMenuInstance = findShapeById(
      'context-menu-' + protocolNode.id,
    );

    const dimensions = getNodeDimensionsByType(node.type);
    const newWidth = dimensions.width + PROTOCOL_PADDING * 2;
    const newHeight = dimensions.height + PROTOCOL_PADDING * 2;

    protocolShapeContextMenuInstance?.x(newWidth);

    protocolShapeRectInstance?.width(newWidth);
    protocolShapeRectInstance?.height(newHeight);

    updateNodePositionInCache(protocolNode, protocolNode.position);
  }

  function shouldEjectNodeFromProtocol(surfaceNode: SurfaceNodeUnion): boolean {
    // Fetch node or just find protocol that this node is in...

    const protocolNodeDraggedInto = fetchProtocolFromContainingNode(
      surfaceNode,
    );

    if (protocolNodeDraggedInto) {
      const protocolShape = findShapeById(protocolNodeDraggedInto?.id);

      const containingNodeShape = findProtocolContainingNodeShape(
        protocolNodeDraggedInto,
      );

      if (protocolNodeDraggedInto && containingNodeShape) {
        return shapesDoNotHaveIntersection(protocolShape, containingNodeShape);
      }
    }

    return false;
  }

  function shapesDoNotHaveIntersection(
    shape: Konva.Node,
    otherShape: Konva.Node,
  ): boolean {
    const rect = shape.getClientRect();
    const otherRect = otherShape.getClientRect();

    return (
      otherRect.x > rect.x + rect.width ||
      otherRect.x + otherRect.width < rect.x ||
      otherRect.y > rect.y + rect.height ||
      otherRect.y + otherRect.height < rect.y
    );
  }

  function shapesHaveIntersection(
    shape: Konva.Node,
    otherShape: Konva.Node,
  ): boolean {
    return shapesDoNotHaveIntersection(shape, otherShape) === false;
  }

  function isHoveringOverProtocolNode(position, protocolNode) {
    //top left corner over protocol node?
    return (
      isPositionOverProtocolNode(position, protocolNode) ||
      //top right corner over protocol node?
      isPositionOverProtocolNode(
        {
          x: position.x + nodeDimensions.width,
          y: position.y,
        },
        protocolNode,
      ) ||
      //bottom left corner over protocol node?
      isPositionOverProtocolNode(
        {
          x: position.x,
          y: position.y + nodeDimensions.height,
        },
        protocolNode,
      ) ||
      //bottom right corner over protocol node?
      isPositionOverProtocolNode(
        {
          x: position.x + nodeDimensions.width,
          y: position.y + nodeDimensions.height,
        },
        protocolNode,
      )
    );
  }

  function isPositionOverProtocolNode(
    position: Position,
    protocolNode: SurfaceProtocolNode,
  ): boolean {
    const nodeDimensions = getNodeDimensionsByType(protocolNode.type);

    return (
      position.x >= protocolNode.position.x &&
      position.x <= protocolNode.position.x + nodeDimensions.width &&
      position.y >= protocolNode.position.y &&
      position.y <= protocolNode.position.y + nodeDimensions.height
    );
  }

  function ejectNodeFromProtocol({
    protocolNode,
    position,
  }: {
    protocolNode: SurfaceProtocolNode;
    position: Position;
  }): Promise<any> {
    const input = {
      node: protocolNode?.contains,
      protocolNode,
      position,
    };

    resizeProtocolToOriginalSize(protocolNode);

    return ejectSurfaceNodeFromSurfaceProtocolNode({
      variables: { input },
    });
  }

  async function placeNodeInProtocol(
    containingNode: SurfaceNodeUnion,
    protocolNodeDraggedInto: SurfaceProtocolNode,
  ): Promise<any> {
    // If node is reference set the original
    // If there are references on same surface...use optimistic update to populate the reference protocols
    // If it's reference protocol that has been dragged into...place the node in original and update the position of that node in respect to original protocol
    // What if that original is on another surface and node has been placed in reference protocol?

    if (protocolNodeDraggedInto) {
      const input = {
        node: containingNode.id,
        protocolNode: protocolNodeDraggedInto.id,
      };

      const positionInput = {
        nodeId: containingNode.id,
        position: {
          x: protocolNodeDraggedInto.position.x + PROTOCOL_PADDING,
          y: protocolNodeDraggedInto.position.y + PROTOCOL_PADDING,
        },
      };

      resizeProtocolToFitNode(containingNode, protocolNodeDraggedInto);

      repositionContainingNodeInProtocol({
        id: containingNode.id,
        position: protocolNodeDraggedInto.position as Position,
      });

      return addSurfaceNodeToSurfaceProtocolNode({
        variables: { input, positionInput },
      });
    }
  }

  function repositionContainingNodeInProtocol({
    position,
    id,
  }: PositinIdInput): void {
    const protocolNodeDraggedInto = getProtocolNodeDraggedInto(position);

    if (protocolNodeDraggedInto) {
      const nodeShape = findProtocolContainingNodeShape(
        protocolNodeDraggedInto,
      );

      if (nodeShape) {
        nodeShape.x(protocolNodeDraggedInto.position.x + PROTOCOL_PADDING);
        nodeShape.y(protocolNodeDraggedInto.position.y + PROTOCOL_PADDING);
      }
    }
  }

  function fetchProtocolFromContainingNode(
    containingNode: SurfaceNodeUnion,
  ): SurfaceProtocolNode | null {
    return (
      fetchProtocolsFromCache().find((protocolNode) => {
        if (protocolNode.contains) {
          const protocolShape = findShapeById(protocolNode.id);
          return protocolNode?.contains?.id === containingNode.id;
        }
        return false;
      }) || null
    );
  }

  async function protocolContainingNodeOnDragEnd(
    containingNode: SurfaceNodeUnion,
    shape: Konva.Node,
  ): Promise<any> {
    // This does not apply to protocol nodes
    if (containingNode.type === SurfaceNodeType.Protocol) {
      return;
    }

    const isContainingNode = fetchProtocolsFromCache().some(
      (protocol) => protocol?.contains?.id === containingNode.id,
    );

    const position = shape?.position() as Position;

    if (isContainingNode) {
      const protocolNode = fetchProtocolsFromCache().find((protocol) => {
        return (
          protocol.contains &&
          isHoveringOverProtocolNode(containingNode.position, protocol) &&
          protocol.contains?.id === containingNode.id
        );
      });

      if (protocolNode && protocolNode.contains) {
        const protocolShape = findShapeById(protocolNode?.id);

        if (shapesDoNotHaveIntersection(shape, protocolShape)) {
          const onDifferentSurface =
            protocolNode?.surface?.id !== protocolNode?.contains?.surface?.id;

          if (onDifferentSurface) {
            addNodeToSurfaceInCache(
              compose(
                set('surface', protocolNode.surface),
                set('position.x', position.x),
                set('position.y', position.y),
              )(containingNode),
            );
          }

          return ejectNodeFromProtocol({ protocolNode, position });
        }

        return repositionContainingNodeInProtocol({
          id: containingNode.id,
          position,
        });
      }
    }

    if (shouldContainNode(position)) {
      const protocolNodeDraggedInto = getProtocolNodeDraggedInto(position);

      if (protocolNodeDraggedInto) {
        // if(protocolNodeDraggedInto.uploader){
        //   return
        // }
        if (protocolNodeDraggedInto.isReference) {
          const originalProtocol = fetchProtocolsFromCache().find(
            (protocol) =>
              protocol.id === protocolNodeDraggedInto?.originalNode?.id,
          );

          if (originalProtocol) {
            resizeProtocolToFitNode(containingNode, protocolNodeDraggedInto);

            const freshProtocolNode = fetchNodeFromCacheById(
              protocolNodeDraggedInto.id,
            );

            const containingShape = findShapeById(containingNode.id);

            const { position } = freshProtocolNode;

            containingShape.x(position?.x + PROTOCOL_PADDING * 2);
            containingShape.y(position?.y + PROTOCOL_PADDING * 2);

            return placeNodeInProtocol(containingNode, originalProtocol);
          }
        }
        return placeNodeInProtocol(containingNode, protocolNodeDraggedInto);
      }
    }
  }

  function findProtocolContainingNodeShape(
    protocol: SurfaceProtocolNode,
  ): Konva.Node | null {
    if (protocol.contains) {
      const protocolShape = findShapeById(protocol.id);

      return findShapeById((shape) => {
        return (
          shape.attrs.id === protocol?.contains?.id &&
          shapesHaveIntersection(shape, protocolShape)
        );
      });
    }

    return null;
  }

  return {
    shouldEjectNodeFromProtocol,
    resizeProtocolToFitNode,
    resizeProtocolToOriginalSize,
    shouldContainNode,
    shouldRepositionContainingNodeInProtocol,
    getProtocolNodeDraggedInto,
    ejectNodeFromProtocol,
    placeNodeInProtocol,
    isPositionOverProtocolNode,
    repositionContainingNodeInProtocol,
    shapesDoNotHaveIntersection,
    shapesHaveIntersection,
    protocolContainingNodeOnDragEnd,
    fetchProtocolsFromCache,
    findProtocolContainingNodeShape,
  };
}
